import { useEffect, useRef, useState } from "react"
import { Spinner } from "../common/Spinner";
import { ReactComponent as SmartIDLogo } from '../../assets/smartIDlogo.svg';
import { smartIdVerificationRequest } from "../../api/requests/auth";
import { IdentityAuthResponseStatus } from "../../api/requests/interfaces/SmartIDLoginResponse";
import { jwtDecode } from "jwt-decode";
import { AccessToken } from "../../store/interfaces/accessToken";
import { setUserState, tokenRefreshInterval } from "../../store/features/authSlice";
import { useAppDispatch } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import { LocalStorageKeys } from "../../constants/localStorageKeys";

export interface SmartIDLoadingProps {
    code: string;
    type: "AUTHENTICATION" | "VERIFICATION"
}

export enum SmartIDVerificationState {
    LOADING,
    CANCELLED,
    ERROR,
    TIMEOUT,
    SUCCESS
}

const SMART_ID_REQUEST_INTERVAL = 2000;

export const SmartIDLoading: React.FC<SmartIDLoadingProps> = (props) => {

    const [timeRemaining, setTimeRemaining] = useState("02:00");
    const [state, setState] = useState<SmartIDVerificationState>(SmartIDVerificationState.LOADING);
    const [authReqTimeout, setAuthReqTimeout] = useState<NodeJS.Timeout>();
    const [timerInterval, setTimerInterval] = useState<NodeJS.Timer>();
    const [fullTimer, setFullTimer] = useState<NodeJS.Timeout>();
    const reqCancelled = useRef<boolean>();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        let timeLeft = 119;

        const interval = setInterval(() => {
            let minutes = "00";
            let seconds = "00";

            const calculatedMins = Math.floor(timeLeft / 60);
            const calculatedSeconds = timeLeft - calculatedMins * 60;
            timeLeft -= 1;

            minutes = calculatedMins.toString().length > 1 ? calculatedMins.toString() : "0" + calculatedMins.toString();
            seconds = calculatedSeconds.toString().length > 1 ? calculatedSeconds.toString() : "0" + calculatedSeconds.toString();
            setTimeRemaining(minutes + ":" + seconds)
        }, 1000)
        setTimerInterval(interval);
        let reqLoop: any;
        reqCancelled.current = false;

        const requestLoop = (isAuthentication: boolean) => {
            const requestTimeout = setTimeout(async () => {
                try {
                    const res = await smartIdVerificationRequest(isAuthentication);
                    if (reqCancelled.current) {
                        return;
                    }

                    switch (res.data.state) {
                        case IdentityAuthResponseStatus.CANCELED:
                            setState(SmartIDVerificationState.CANCELLED);
                            clearInterval(interval);
                            const timeout1 = setTimeout(() => {
                                if (isAuthentication) {
                                    window.location.reload();
                                } else {
                                    navigate('/profile');
                                }
                                clearTimeout(timeout1);
                                clearTimeout(requestTimeout);
                            }, 2000)
                            break;
                        case IdentityAuthResponseStatus.OK:
                            setState(SmartIDVerificationState.SUCCESS);
                            if (isAuthentication) {
                                const accessToken = res.headers["authorization"].split(" ")[1];
                                const decodedToken = jwtDecode<AccessToken>(accessToken);
                                dispatch(setUserState({
                                    role: decodedToken.role,
                                    accessToken: accessToken,
                                    id: Number(decodedToken.sub)
                                }))
                                localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, res.headers["authorization"].split(" ")[1]);
                                localStorage.setItem(LocalStorageKeys.USER_ROLE, decodedToken.role);
                                localStorage.setItem(LocalStorageKeys.USER_ID, decodedToken.sub);
                                dispatch(tokenRefreshInterval());
                            }
                            clearInterval(interval);
                            const timeout = setTimeout(() => {
                                if (isAuthentication) {
                                    navigate('/');
                                } else {
                                    navigate('/profile');
                                }
                                clearTimeout(timeout);
                                clearTimeout(requestTimeout);
                            }, 2000)
                            break;
                        default:
                            setState(SmartIDVerificationState.LOADING);
                            requestLoop(isAuthentication);
                    }
                } catch {
                    setState(SmartIDVerificationState.ERROR);
                    clearInterval(interval);
                    clearTimeout(requestTimeout);
                    const timeout2 = setTimeout(() => {
                        if (isAuthentication) {
                            window.location.reload();
                        } else {
                            navigate('/profile');
                        }
                        clearTimeout(timeout2);
                    }, 2000)
                }
            }, SMART_ID_REQUEST_INTERVAL)
            reqLoop = requestTimeout;
            setAuthReqTimeout(() => requestTimeout);
        }

        requestLoop(props.type === "AUTHENTICATION");

        const timeOut = setTimeout((inter) => {
            clearInterval(inter);
            if (authReqTimeout) {
                clearTimeout(authReqTimeout);
            }
            reqCancelled.current = true;
            setState(SmartIDVerificationState.TIMEOUT);
        }, 120000, interval)
        setFullTimer(timeOut);

        return () => {
            clearTimeout(timeOut);
            clearInterval(interval);
            if (authReqTimeout) {
                clearTimeout(authReqTimeout);
            }
            clearTimeout(reqLoop);
        }
    }, [])

    const handleRequestLoopCancelClick = () => {
        if (authReqTimeout) {
            clearTimeout(authReqTimeout);
        }
        reqCancelled.current = true;
        clearInterval(timerInterval);
        clearTimeout(fullTimer);
        setState(SmartIDVerificationState.CANCELLED);
        const timeOutExit = setTimeout(() => {
            if (props.type === "AUTHENTICATION") {
                window.location.reload();
            } else {
                navigate('/profile');
            }
            clearTimeout(timeOutExit);
        }, 2000)
    }

    return (
        <div className="shadow-md rounded-lg py-2 w-full lg:w-[300px] flex flex-col items-center bg-white">
            <SmartIDLogo style={{transform: "scale(0.7)"}}/>
            <div className="text-sm">Kood:</div>
                <div className="text-3xl font-bold">{props.code}</div>
            <div className="flex relative justify-center align-middle items-center mt-6 mb-1">
                {
                    state === SmartIDVerificationState.LOADING ?
                    <Spinner size="large"/>
                    :
                    <div className={`w-[70px] h-[70px] border-[5px] rounded-full ${state === SmartIDVerificationState.CANCELLED ? "border-red-500" : 
                    state === SmartIDVerificationState.TIMEOUT ? "border-yellow-500" : "border-green-500"}`}>
                    </div>
                }
                <div className="absolute">
                    {timeRemaining}
                </div>
            </div>
            {
                state === SmartIDVerificationState.CANCELLED ?
                <div className="text-red-500">Autentimisest keelduti</div>
                :
                state === SmartIDVerificationState.TIMEOUT ?
                <div className="text-yellow-500">Päring aegunud</div>
                :
                state === SmartIDVerificationState.SUCCESS ?
                <div className="text-green-500">Edukalt sisse logitud</div>
                :
                state === SmartIDVerificationState.ERROR ?
                <div className="text-red-500">Autentimisel esines viga</div>
                :
                <></>
            }
            {
                state === SmartIDVerificationState.LOADING ?
                <div className="flex align-middle items-center mt-4">
                    <div className="p-2 border-2 border-primary text-primary rounded-lg hover:border-primary_darker cursor-pointer"
                    onClick={() => handleRequestLoopCancelClick()}>Tühista</div>
                </div>
                :
                <></>
            }
        </div>
    )
}